import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';

const ENDPOINT_URL = '/users/me';

// This endpoint targets the authenticated user; no UUID path param is required
export type DeleteUserInput = void;

/**
 * Deletes the currently authenticated user (no UUID required).
 */
export const useDeleteUser = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [succeeded, setSucceeded] = useState<boolean | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setSucceeded(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}` , {
                method: 'DELETE',
                headers: getHeaders(),
                body: JSON.stringify(getCommonParameters()),
            });

            if (!response.ok) {
                let err: ApiError;
                try {
                    const json = await response.json();
                    err = new Error(json.message || '') as ApiError;
                    err.statusCode = response.status;
                } catch (e) {
                    err = new Error() as ApiError;
                    err.statusCode = response.status;
                    err.message = '';
                }
                throw err;
            }

            // Only consider as succeeded when API returns 200 OK explicitly
            const isOk200 = response.status === 200;
            try {
                const body = await response.json();
                const message =
                    typeof (body as { message?: string })?.message === 'string'
                        ? (body as { message?: string }).message
                        : typeof (body as { data?: { message?: string } })?.data?.message === 'string'
                          ? (body as { data?: { message?: string } }).data?.message ?? null
                          : null;
                setSuccess(message ?? null);
            } catch {
                // no JSON body
                setSuccess(null);
            }
            setSucceeded(isOk200);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            const fallback = typeof err?.statusCode === 'number' ? `Request failed (${err.statusCode})` : 'Request failed';
            setError(typeof err?.message === 'string' && err.message.trim() ? err.message : fallback);
            setSucceeded(false);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, success, lastResponseTime, succeeded };
};
